import { useContext } from 'react'
import PropTypes from 'prop-types'
import swal from 'sweetalert2'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTrashAlt } from '@fortawesome/pro-light-svg-icons'

import {
  Box,
  Button,
  Form,
  ModalContext,
  SectionDivider,
  SidebarModal,
  Text,
  ToggleButton,
} from '@domain-group/suit-theme'

import { digObject } from '@domain-group/javascript-utils'

import defaultRequestOptions from '@sections/Admin/packs/SystemManager/defaultRequestOptions'

import useProviderDisclaimer, { useProviderDisclaimerForm } from '@hooks/useProviderDisclaimer'

import EntitySelector from '@components/EntitySelector'

const confirmDelete = (deleteProviderDisclaimer, deleteFn, closeModal) => {
  const deleteProviderDisclaimerPayload = {
    callbacks: {
      action: deleteFn,
      afterAction: closeModal,
    },
    toastText: 'Disclaimer Deleted',
  }

  swal.fire({
    confirmButtonColor: '#DD6B55',
    confirmButtonText: 'Yes, delete it.',
    icon: 'warning',
    showCancelButton: true,
    text: 'Are you sure?',
    title: 'Delete Disclaimer?',
  }).then(({ value }) => {
    if (value){
      deleteProviderDisclaimer(deleteProviderDisclaimerPayload)
    }
  })
}

const createOrUpdateProviderDisclaimer = (entityState, props, createFn, updateFn) => {
  const {
    callbacks: { closeModal, createProviderDisclaimer, updateProviderDisclaimer },
  } = props

  const text = entityState.id ? 'Updated' : 'Created'
  const actionFn = entityState.id ? updateFn : createFn

  const providerDisclaimerPayload = {
    callbacks: {
      action: actionFn,
      afterAction: closeModal,
    },
    entityParams: entityState,
    requestOptions: defaultRequestOptions.providerDisclaimer,
    toastText: `Disclaimer ${text}`,
  }

  if (entityState.id) return updateProviderDisclaimer(providerDisclaimerPayload)

  return createProviderDisclaimer(providerDisclaimerPayload)
}

const CreateOrEditProviderDisclaimerModal = (props) => {
  const { callbacks, modalKey, showModal } = props
  const { closeModal, deleteProviderDisclaimer } = callbacks

  const modalContext = useContext(ModalContext)
  const { modalData } = modalContext

  const modalPayload = digObject(modalData, modalKey, {})
  const { providerDisclaimer } = modalPayload

  const {
    callbacks: {
      createProviderDisclaimer: createFn,
      deleteProviderDisclaimer: deleteFn,
      updateProviderDisclaimer: updateFn,
    },
    creating,
    deleting,
    updating,
  } = useProviderDisclaimer(providerDisclaimer)

  const {
    entityState,
    entityState: {
      acceptance_required,
      disclaimer_text,
      entity_id,
      id,
    },
    errors,
    handlers,
    saveEnabled,
    setEntityState,
  } = useProviderDisclaimerForm(providerDisclaimer, { validateOn: 'change' })

  const saveOrCreateButtonText = id ? 'Save Changes' : 'Create Disclaimer'

  return (
    <SidebarModal
      callbacks={callbacks}
      modalKey={modalKey}
      showModal={showModal}
      size="small"
    >
      <SidebarModal.Header
        callbacks={callbacks}
        title={id ? 'Edit' : 'Create'}
        titleSecondLine="Disclaimer"
      />

      <SidebarModal.Content>
        <Box flexDirection="column" flexShrink={0}>

          <EntitySelector
            boxProps={{ marginBottom: 'large' }}
            callbacks={{
              selectItem: serviceProduct => setEntityState({
                entity_id: serviceProduct ? serviceProduct.id : null,
                entity_type: 'ServiceProduct',
              }),
            }}
            endpoint="service_products"
            entityKey="serviceProducts"
            errorMessage={errors.entity_id}
            label="* Assign to Product"
            searchRequestOptions={{
              include_service_product_service: true,
            }}
            secondaryTextKey="service.title"
            selectedItemId={entity_id}
            showEntityId
          />

          <Form.Field
            direction="column"
            errorMessage={errors.disclaimer_text}
            label="* Disclaimer Contents"
            marginBottom="large"
          >
            <textarea
              name="disclaimer_text"
              style={{ height: 120 }}
              type="text"
              value={disclaimer_text}
              {...handlers}
            />
          </Form.Field>

          <SectionDivider marginTop="large">DISCLAIMER SETTINGS</SectionDivider>

          <Box flexDirection="row" marginTop="medium" alignItems="center">
            <Box flexDirection="column" width="80%">
              <Text fontSize="small" marginRight="medium">Acceptance is Required</Text>

              <Text
                color="bodyFontLightColor"
                fontSize="xsmall"
                marginTop="small"
                variant="twoLineEllipsis"
              >
                User cannot proceed with order without accepting the disclaimer
              </Text>
            </Box>

            <ToggleButton
              boxProps={{ height: '60%' }}
              onClick={() => setEntityState({ acceptance_required: !acceptance_required })}
              toggleOn={acceptance_required}
              toggleOnText="Yes"
              toggleOffText="No"
            />
          </Box>

          {id && (
            <Form.Field direction="column" label="More Options" boxProps={{ marginTop: 20 }}>
              <Button
                buttonStyle="secondaryUtility"
                icon={<FontAwesomeIcon icon={faTrashAlt} />}
                loading={deleting}
                onClick={() => confirmDelete(deleteProviderDisclaimer, deleteFn, closeModal)}
                size="medium"
              >
                Delete Disclaimer
              </Button>
            </Form.Field>
          )}
        </Box>
      </SidebarModal.Content>

      <SidebarModal.Footer>
        <Button
          buttonStyle="primaryCreate"
          disabled={!saveEnabled}
          loading={creating || updating}
          onClick={() => createOrUpdateProviderDisclaimer(entityState, props, createFn, updateFn)}
          size="large"
        >
          {saveEnabled ? saveOrCreateButtonText : 'Complete Fields'}
        </Button>
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

CreateOrEditProviderDisclaimerModal.propTypes = {
  callbacks: PropTypes.object.isRequired,
  modalKey: PropTypes.string,
  showModal: PropTypes.bool,
}

CreateOrEditProviderDisclaimerModal.defaultProps = {
  modalKey: 'CreateOrEditProviderDisclaimerModal',
}

const LazyLoadedModal = props => (
  <SidebarModal.RenderController {...props}>
    <CreateOrEditProviderDisclaimerModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
